import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, ExternalLink, Film, FileSpreadsheet, FileJson, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { fehlerText, api, deleteClip, clipExportUrl, downloadFile, API } from "@/lib/api";
import { toast } from "sonner";
import HelpButton from "@/components/HelpButton";

export default function Sequences() {
  const navigate = useNavigate();
  const [clips, setClips] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/clips");
      setClips(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onDelete = async (id) => {
    if (!confirm("Delete this sequence?")) return;
    // Optimistic UI: remove the row immediately for instant feedback.
    setClips((prev) => prev.filter((c) => c.id !== id));
    try {
      await deleteClip(id);
      toast.success("Sequence deleted");
    } catch (err) {
      const msg = fehlerText(err) || err?.message || "Delete failed";
      toast.error(`Could not delete sequence: ${msg}`);
    } finally {
      load();
    }
  };

  const triggerDownload = (id, kind) => {
    // Backend's Content-Disposition already sets a clean filename; the `download`
    // attribute is just a UX hint that browsers may override. Normalise the ZIP
    // bundle name so the hint matches the server's chosen filename.
    const ext = kind === "all.zip" ? "zip" : kind;
    const a = document.createElement("a");
    a.href = clipExportUrl(id, kind);
    a.download = `seq_${id.slice(0, 8)}.${ext}`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const filtered = clips.filter((c) =>
    !q ||
    c.name.toLowerCase().includes(q.toLowerCase()) ||
    c.match_name?.toLowerCase().includes(q.toLowerCase()) ||
    c.id.startsWith(q) ||
    (c.offense || "").toLowerCase().includes(q.toLowerCase()) ||
    (c.defense || "").toLowerCase().includes(q.toLowerCase())
  );

  const stats = {
    total: clips.length,
    tracked: clips.filter((c) => c.tracked).length,
    labeled: clips.filter((c) => c.labeled).length,
    exported: clips.filter((c) => c.status === "exported").length,
  };

  return (
    <div className="min-h-screen" data-testid="sequences-page">
      <header className="px-8 pt-8 pb-6 border-b border-border">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
              Database · Sequences
            </div>
            <h1 className="font-display font-black uppercase text-4xl tracking-tight">
              All Sequences
            </h1>
            <div className="mt-2 flex items-center gap-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              <span><span className="text-foreground">{stats.total}</span> total</span>
              <span>·</span>
              <span><span className="text-primary">{stats.tracked}</span> tracked</span>
              <span>·</span>
              <span><span className="text-primary">{stats.labeled}</span> labeled</span>
              <span>·</span>
              <span><span className="text-primary">{stats.exported}</span> exported</span>
            </div>
          </div>
          <div className="flex gap-3 items-center">
            <Input
              data-testid="sequences-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, match, offense…"
              className="w-72 bg-secondary border-border focus:border-primary"
            />
            <HelpButton topic="sequences_view" />
          </div>
        </div>
      </header>

      <div className="px-8 py-6">
        <div className="border border-border bg-card rounded-sm overflow-hidden">
          <Table data-testid="sequences-table">
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border">
                {["ID", "Name", "Match", "Format · Div", "Cut", "Offense", "Defense", "Phase", "Tags", "Status", "Actions"].map((h, i) => (
                  <TableHead
                    key={h}
                    className={`font-mono text-[10px] uppercase tracking-widest text-muted-foreground ${i === 10 ? "text-right" : ""}`}
                  >
                    {h}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={11} className="text-center text-muted-foreground py-12 font-mono text-xs">Loading…</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={11} className="text-center text-muted-foreground py-16">
                  {clips.length === 0 ? "No sequences yet — create a match and start cutting." : "No matches for that search."}
                </TableCell></TableRow>
              ) : filtered.map((c) => (
                <TableRow
                  key={c.id}
                  data-testid={`sequence-row-${c.id}`}
                  className="border-border hover:bg-secondary/50"
                >
                  <TableCell className="font-mono text-xs text-primary">#{c.id.slice(0, 8)}</TableCell>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell>
                    <button
                      onClick={() => navigate(`/admin/wizard/${c.sequence_id}`)}
                      className="text-sm text-foreground hover:text-primary text-left truncate max-w-[220px] inline-block"
                      title={c.match_name}
                    >
                      {c.match_name}
                    </button>
                  </TableCell>
                  <TableCell className="font-mono text-[10px] text-muted-foreground">
                    {c.match_format || "—"} · {c.match_gender || "—"}
                    {c.gender_split && <span className="text-primary"> · {c.gender_split}</span>}
                  </TableCell>
                  <TableCell className="font-mono text-[10px] tabular-nums whitespace-nowrap">
                    {c.start_s.toFixed(2)}→{c.end_s.toFixed(2)}s
                    <span className="ml-1 text-primary">({c.duration_s.toFixed(1)}s)</span>
                  </TableCell>
                  <TableCell className="text-sm">{c.offense || <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="text-sm">{c.defense || <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="text-sm">{c.game_phase || <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell>
                    {c.tags?.length > 0 ? (
                      <div className="flex gap-1 flex-wrap max-w-[160px]">
                        {c.tags.slice(0, 3).map((t) => (
                          <Badge key={t} className="bg-accent text-primary border border-primary/30 font-mono text-[9px] uppercase tracking-widest px-1.5 py-0">{t}</Badge>
                        ))}
                        {c.tags.length > 3 && <span className="text-[10px] text-muted-foreground">+{c.tags.length - 3}</span>}
                      </div>
                    ) : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell><StatusPill status={c.status} /></TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {c.tracked && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => triggerDownload(c.id, "mp4")} className="h-7 w-7 p-0 hover:bg-accent hover:text-primary" title="MP4">
                          <Film className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => triggerDownload(c.id, "csv")} className="h-7 w-7 p-0 hover:bg-accent hover:text-primary" title="CSV">
                          <FileSpreadsheet className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => triggerDownload(c.id, "json")} className="h-7 w-7 p-0 hover:bg-accent hover:text-primary" title="JSON">
                          <FileJson className="w-3.5 h-3.5" />
                        </Button>
                        <Button data-testid={`row-zip-${c.id}`} size="sm" variant="ghost" onClick={() => triggerDownload(c.id, "all.zip")} className="h-7 w-7 p-0 hover:bg-accent hover:text-primary" title="MP4 + CSV + JSON as ZIP">
                          <Package className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => navigate(`/admin/wizard/${c.sequence_id}`)} className="h-7 w-7 p-0 hover:bg-accent hover:text-primary" title="Open in lab">
                      <ExternalLink className="w-3.5 h-3.5" />
                    </Button>
                    <Button data-testid={`delete-sequence-${c.id}`} size="sm" variant="ghost" onClick={() => onDelete(c.id)} title="Delete sequence" className="h-7 w-7 p-0 hover:bg-destructive/20 hover:text-destructive">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    draft: { c: "bg-secondary text-muted-foreground border-border", l: "Draft" },
    tracking: { c: "bg-primary/15 text-primary border-primary/30 animate-pulse", l: "Tracking" },
    tracked: { c: "bg-primary/15 text-primary border-primary/30", l: "Tracked" },
    labeled: { c: "bg-primary/15 text-primary border-primary/30", l: "Labeled" },
    exported: { c: "bg-primary text-primary-foreground border-primary", l: "Exported" },
    failed: { c: "bg-destructive/15 text-destructive border-destructive/30", l: "Failed" },
  };
  const { c, l } = map[status] || map.draft;
  return <Badge className={`${c} border font-mono text-[10px] uppercase tracking-widest`}>{l}</Badge>;
}
